/*
 * Manual colour-scheme override (light/dark/system), persisted in localStorage.
 *
 * The theme tokens follow the OS via `color-scheme: light dark` + `light-dark()` (see
 * theme.css). Forcing a scheme narrows `color-scheme` on the root element, which
 * re-resolves every `light-dark()` token at once, and sets `data-color-scheme` for
 * selector-based CSS (the `dark` variant in global.css). `system` clears both, so the
 * OS preference applies again with no JS involved.
 *
 * `light` is the default: when nothing is persisted (new user, or after logout clears
 * storage) the apps render light regardless of the OS. `system` is therefore stored
 * explicitly rather than as the absence of a value, so an opt-in to follow the OS sticks.
 *
 * Consumers that resolve the scheme themselves and cannot see the root attribute
 * (JS `matchMedia` users like React Flow's `colorMode`, whole stylesheets like the
 * PrimeReact lara variants) hook in via `subscribeColorScheme` / `mountColorSchemeStylesheet` instead.
 */

export const COLOR_SCHEMES = ['system', 'light', 'dark'] as const;

export type ColorScheme = (typeof COLOR_SCHEMES)[number];

export const STORAGE_KEY = 'color-scheme';

/** Root attribute driving selector-based CSS. Hardcoded by the `dark` custom variant in global.css. Keep in sync. */
export const DATA_ATTRIBUTE = 'data-color-scheme';

/** Marks a stylesheet as scheme-specific. Its `media` attribute tracks the active scheme. */
export const MEDIA_ATTRIBUTE = 'data-color-scheme-media';

/** Narrow an arbitrary value (e.g. a stored string) to a known scheme. Absent/unknown means the light default. */
export const toColorScheme = (value: unknown): ColorScheme =>
  COLOR_SCHEMES.includes(value as ColorScheme) ? (value as ColorScheme) : 'light';

export const getStoredColorScheme = (): ColorScheme => {
  try {
    return toColorScheme(localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'light';
  }
};

/** Persists the chosen scheme, including `system`, so it survives reloads. No stored value means the light default. */
export const persistColorScheme = (colorScheme: ColorScheme) => {
  try {
    localStorage.setItem(STORAGE_KEY, colorScheme);
  } catch {
    // localStorage unavailable. The scheme still applies for this session.
  }
};

/** Media that activates a `targetScheme` stylesheet under the given active scheme. */
const schemeMedia = (targetScheme: 'light' | 'dark', activeScheme: ColorScheme): string => {
  if (activeScheme === 'system') {
    return `(prefers-color-scheme: ${targetScheme})`;
  }
  return activeScheme === targetScheme ? 'all' : 'not all';
};

/** Re-targets every stylesheet mounted via `mountColorSchemeStylesheet` to the active scheme. */
const syncColorSchemeStylesheets = (colorScheme: ColorScheme) => {
  const sheets = document.querySelectorAll<HTMLStyleElement | HTMLLinkElement>(`[${MEDIA_ATTRIBUTE}]`);
  for (const sheet of sheets) {
    const targetScheme = sheet.getAttribute(MEDIA_ATTRIBUTE);
    if (targetScheme === 'light' || targetScheme === 'dark') {
      sheet.media = schemeMedia(targetScheme, colorScheme);
    }
  }
};

/** Applies a scheme to the document. `system` removes the override so the OS preference applies. */
export const applyColorScheme = (colorScheme: ColorScheme) => {
  const root = document.documentElement;
  if (colorScheme === 'system') {
    root.style.removeProperty('color-scheme');
    root.removeAttribute(DATA_ATTRIBUTE);
  } else {
    root.style.colorScheme = colorScheme;
    root.setAttribute(DATA_ATTRIBUTE, colorScheme);
  }
  syncColorSchemeStylesheets(colorScheme);
};

/**
 * Mounts CSS that applies under exactly one scheme (e.g. a PrimeReact lara theme
 * variant). The style tag's `media` attribute follows the active scheme: the OS
 * preference under `system` (pure CSS, no JS on OS changes), forced on/off otherwise.
 * `layer` wraps the CSS in `@layer <name>` so it keeps its cascade position.
 */
export const mountColorSchemeStylesheet = (css: string, targetScheme: 'light' | 'dark', layer?: string) => {
  const sheet = document.createElement('style');
  sheet.setAttribute(MEDIA_ATTRIBUTE, targetScheme);
  sheet.media = schemeMedia(targetScheme, getColorScheme());
  sheet.textContent = layer ? `@layer ${layer} {\n${css}\n}` : css;
  document.head.appendChild(sheet);
};

let currentColorScheme: ColorScheme | undefined;
const listeners = new Set<() => void>();

/** The active scheme. Reads the persisted value once. `setColorScheme` keeps it current. */
export const getColorScheme = (): ColorScheme => {
  currentColorScheme ??= getStoredColorScheme();
  return currentColorScheme;
};

/** Updates the store, applies the scheme, and broadcasts to `subscribeColorScheme` listeners. */
const adoptColorScheme = (colorScheme: ColorScheme) => {
  currentColorScheme = colorScheme;
  applyColorScheme(colorScheme);
  for (const listener of listeners) {
    listener();
  }
};

/** Applies, persists, and broadcasts a scheme change to `subscribeColorScheme` listeners. */
export const setColorScheme = (colorScheme: ColorScheme) => {
  persistColorScheme(colorScheme);
  adoptColorScheme(colorScheme);
};

/** Subscribes to scheme changes made via `setColorScheme`. Returns an unsubscribe function. */
export const subscribeColorScheme = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

let crossTabSyncStarted = false;

/**
 * Adopts scheme changes persisted by other tabs (`storage` events only fire in tabs that
 * did not write). A cleared key, including a wholesale `localStorage.clear()` on logout,
 * falls back to the light default.
 */
const startCrossTabSync = () => {
  if (crossTabSyncStarted) {
    return;
  }
  crossTabSyncStarted = true;
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) {
      return;
    }
    const colorScheme = toColorScheme(event.newValue);
    if (colorScheme !== currentColorScheme) {
      adoptColorScheme(colorScheme);
    }
  });
};

/**
 * Applies the persisted colour scheme to the document, seeds the in-memory store, and
 * starts following changes made in other tabs. Call synchronously at the top of an
 * app's entry module, before the first render, so a forced scheme paints without a
 * flash of the OS scheme.
 */
export const applyStoredColorScheme = () => {
  currentColorScheme = getStoredColorScheme();
  applyColorScheme(currentColorScheme);
  startCrossTabSync();
};
